import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { resolveCanvasFontFamily, textChoiceStyles } from "@ai-teach/canvas-styles";
import type { TextChoiceElement } from "@ai-teach/types";
import { useEffect, useRef, useState } from "react";
import { QuestionCheckbox } from "./QuestionCheckbox";
import { gradedFillStyle } from "./gradedStyles";

interface TextChoiceRendererProps {
  element: TextChoiceElement;
  isLiveMode?: boolean;
  isPicked?: boolean | undefined;
  isChoiceFrozen?: boolean | undefined;
  isFrozen?: boolean | undefined;
}

export function TextChoiceRenderer({
  element,
  isLiveMode = false,
  isPicked,
  isChoiceFrozen,
  isFrozen,
}: TextChoiceRendererProps) {
  const { selectedId } = useAppSelector((state) => state.slideEditor.present);

  const isSelected = selectedId === element.id;

  const dispatch = useAppDispatch();

  // First click selects (and drags); a click while already selected opens editing.
  const [isEditing, setIsEditing] = useState(false);
  const pointerDownAt = useRef<{ x: number; y: number } | null>(null);
  const textDivRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isSelected) setIsEditing(false);
  }, [isSelected]);

  const handleToggleIsCorrectAnswer = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    dispatch(
      updateElementProps({
        id: element.id ?? "",
        props: {
          isCorrectAnswer: !element.isCorrectAnswer,
        },
      })
    );
  };

  // A choice element is one option of a question the canvas can't see the shape of, so an unpicked
  // correct answer is only revealed once every choice of its type is locked.
  const graded = gradedFillStyle(
    element.isCorrectAnswer
      ? isFrozen || (isPicked && isChoiceFrozen)
        ? "correct"
        : undefined
      : isPicked && isChoiceFrozen
        ? "wrong"
        : undefined
  );

  return (
    <div
      style={{
        ...textChoiceStyles.outerDiv,
        borderRadius: element.border.cornerRadius,
        backgroundColor: graded.backgroundColor ?? element.backgroundColor ?? "transparent",
        pointerEvents: isLiveMode ? "none" : "auto",
      }}
    >
      {/* Checkbox - positioned relative to outer container */}
      {!isLiveMode && (
        <QuestionCheckbox
          isCorrectAnswer={element.isCorrectAnswer}
          handleToggleIsCorrectAnswer={handleToggleIsCorrectAnswer}
          position="top-left"
        />
      )}

      {/* Text content - with overflow hidden */}
      <div
        ref={textDivRef}
        contentEditable={!isLiveMode && isEditing}
        // Only while editing does a press on the text skip Moveable (to place the caret).
        data-no-moveable-drag={!isLiveMode && isEditing ? "" : undefined}
        suppressContentEditableWarning
        onPointerDown={(e) => {
          pointerDownAt.current = { x: e.clientX, y: e.clientY };
        }}
        onClick={(e) => {
          if (isLiveMode || isEditing || !isSelected) return;
          const down = pointerDownAt.current;
          const moved = !!down && Math.hypot(e.clientX - down.x, e.clientY - down.y) > 4;
          if (!moved) {
            setIsEditing(true);
            // e.currentTarget is nulled once the handler returns (React event pooling) — use
            // the ref, which stays valid into the next frame.
            requestAnimationFrame(() => textDivRef.current?.focus());
          }
        }}
        style={{
          ...textChoiceStyles.textContainer,
          fontSize: element.style?.fontSize,
          // Boldness is baked into the chosen font FILE (resolveCanvasFontFamily), not this
          // property — also passing "bold" here would ask the browser to synthesize extra weight
          // on top of an already-bold face.
          fontWeight: "normal",
          color: graded.color ?? element.style?.color,
          fontFamily: resolveCanvasFontFamily(element.style?.fontFamily, element.style?.fontWeight),
          textAlign: (element.style?.textAlign || "left") as React.CSSProperties["textAlign"],
          textDecoration: element.style?.textDecoration || "none",
          fontStyle: element.style?.fontStyle || "normal",
          // Explicit, deterministic canvas line-height — was relying on CSS `normal`, which
          // measured at 1.5x for this font/size. Pinning it so mobile can reproduce the exact
          // same ratio instead of drifting with native font metrics.
          lineHeight: 1.5,
        }}
        onKeyDown={
          !isLiveMode
            ? (e) => {
                if (e.key === "Tab" || e.key === "Escape") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }
            : undefined
        }
        onBlur={(e) => {
          setIsEditing(false);
          dispatch(
            updateElementProps({
              id: element.id ?? "",
              props: {
                text: e.target.innerText,
              },
            })
          );
        }}
      >
        {element.text}
      </div>
    </div>
  );
}
